import { z } from 'zod';
import { isoDateSchema } from './common.js';

/**
 * Medição (periodic measurement billing). The server is the source of
 * truth for per-stage `percent_billed_to_date`; the client only sends the
 * delta for this period.
 */
export const measurementStatusSchema = z.enum([
  'draft',
  'approved',
  'sent',
  'paid',
  'rejected',
]);
export type MeasurementStatus = z.infer<typeof measurementStatusSchema>;

export const measurementItemSchema = z.object({
  id: z.string(),
  measurementId: z.string(),
  stageId: z.string(),
  stageNameSnapshot: z.string(),
  percentBilled: z.number().min(0).max(100),
  unitPriceCents: z.number().int().min(0),
  subtotalCents: z.number().int().min(0),
  createdAt: z.string(),
});
export type MeasurementItem = z.infer<typeof measurementItemSchema>;

export const measurementSchema = z.object({
  id: z.string(),
  projectId: z.string(),
  periodStart: isoDateSchema,
  periodEnd: isoDateSchema,
  status: measurementStatusSchema,
  totalCents: z.number().int().min(0),
  boletimR2Key: z.string().nullable(),
  notes: z.string().nullable(),
  approvedBy: z.string().nullable(),
  approvedByName: z.string().nullable(),
  approvedAt: z.string().nullable(),
  paidAt: z.string().nullable(),
  createdBy: z.string(),
  createdByName: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  items: z.array(measurementItemSchema).default([]),
});
export type Measurement = z.infer<typeof measurementSchema>;

export const createMeasurementItemInputSchema = z.object({
  stageId: z.string().min(1),
  percentBilled: z.number().min(0).max(100),
  unitPriceCents: z.number().int().min(0).max(1_000_000_000_00),
});

export const createMeasurementRequestSchema = z
  .object({
    periodStart: isoDateSchema,
    periodEnd: isoDateSchema,
    notes: z.string().trim().max(2000).optional(),
    items: z.array(createMeasurementItemInputSchema).min(1).max(200),
  })
  .refine((v) => v.periodStart <= v.periodEnd, {
    message: 'periodStart must be on or before periodEnd',
    path: ['periodEnd'],
  });
export type CreateMeasurementRequest = z.infer<
  typeof createMeasurementRequestSchema
>;

export const approveMeasurementRequestSchema = z.object({
  notes: z.string().trim().max(2000).optional(),
});
export type ApproveMeasurementRequest = z.infer<
  typeof approveMeasurementRequestSchema
>;
